import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "@/lib/toast";

const IMG_EXT = ["jpg", "jpeg", "png", "gif", "webp", "heic"];
export const previewKind = (name) => {
  const ext = String(name || "").split(".").pop().toLowerCase();
  return ext === "pdf" ? "pdf" : IMG_EXT.includes(ext) ? "image" : null;
};

/** Aperçu intégré (PDF / image) d'un fichier servi par l'API — spec iter306. */
export default function FilePreviewDialog({ open, onOpenChange, url, filename }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const kind = previewKind(filename);

  useEffect(() => {
    if (!open || !url) return undefined;
    let alive = true;
    let created = null;
    setLoading(true);
    api.get(url, { responseType: "blob" })
      .then((r) => {
        const type = kind === "pdf" ? "application/pdf" : r.data.type;
        created = URL.createObjectURL(type ? new Blob([r.data], { type }) : r.data);
        if (alive) setBlobUrl(created);
      })
      .catch((err) => { toast.error("Aperçu impossible", { description: err.response?.data?.detail || "" }); onOpenChange(false); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; if (created) URL.revokeObjectURL(created); setBlobUrl(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, url]);

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl; a.download = filename || "fichier";
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-[#3A3A3A] p-0 flex flex-col gap-0"
        style={{ height: "92vh", width: "calc(100vw - 140px)", maxWidth: "calc(100vw - 140px)", left: "calc(50% + 45px)" }} onClick={(e) => e.stopPropagation()} data-testid="file-preview-dialog">
        <DialogHeader className="px-4 pt-3 pb-2 border-b border-[#3A3A3A] shrink-0">
          <DialogTitle className="flex items-center justify-between text-sm">
            <span className="truncate min-w-0 flex-1 text-left">
              <span className="text-muted-foreground mr-2">Aperçu —</span>
              <span className="font-semibold" data-testid="file-preview-name">{filename}</span>
            </span>
            <Button size="sm" variant="outline" className="h-7 text-xs mr-6" onClick={download} disabled={!blobUrl} data-testid="file-preview-download">
              <Download size={13} className="mr-1.5" /> Télécharger
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 flex items-center justify-center" style={{ background: "#111" }}>
          {loading && <Loader2 className="animate-spin text-muted-foreground" size={22} />}
          {!loading && blobUrl && kind === "pdf" && (
            <iframe title={filename} src={blobUrl} className="w-full h-full border-0" data-testid="file-preview-iframe" />
          )}
          {!loading && blobUrl && kind === "image" && (
            <img src={blobUrl} alt={filename} className="max-w-full max-h-full object-contain" data-testid="file-preview-image" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
